import React, {useMemo, useState, useCallback} from 'react';
import {Platform, View, Text, LayoutChangeEvent} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {createStepperStyles} from '../../../../shared/components/ui/Stepper/Stepper.styles';
import {StepperProps} from '../../../../shared/components/ui/Stepper/StepperProps';
import type {StepCellProps} from '../../../../features/CreateFlow/CreateFlowProps';
import {
  getStepState,
  computeLabelMaxWidth,
} from '../../../../features/CreateFlow/CreateFlowController';
import {_LABEL_FONT_SIZE} from '../../../../features/CreateFlow/CreateFlowConstant';
import StepperCompletionTickIcon from '../../../../shared/assets/icons/StepperCompletionTickIcon';
import Typography from '../../../../shared/constants/Typography';

/** Diameter of a step circle (matches styles.circle width/height). */
const CIRCLE_SIZE = Typography.width.width32;

/**
 * The stepper label renders at fontSize12. computeLabelMaxWidth was calibrated
 * for _LABEL_FONT_SIZE (fontSize16), so scale its output down proportionally.
 */
const LABEL_FONT_SIZE = Typography.fontSize.fontSize16;
const LABEL_SCALE = LABEL_FONT_SIZE / _LABEL_FONT_SIZE; // 12 / 16 = 0.75

/**
 * StepCircle — renders only the 32 px circle badge for a single step.
 */
const StepCircle: React.FC<StepCellProps> = ({step, state, styles, testID}) => {
  return (
    <View
      style={[
        styles.circle,
        state === 'active' && styles.circleActive,
        state === 'completed' && styles.circleCompleted,
        state === 'inactive' && styles.circleInactive,
      ]}
      importantForAccessibility="no-hide-descendants"
      accessibilityElementsHidden={true}
      testID={testID ? `${testID}-step-${step.id}` : undefined}>
      {state === 'completed' ? (
        <StepperCompletionTickIcon />
      ) : (
        <Text
          style={[
            styles.circleText,
            state === 'active' && styles.circleTextActive,
            state === 'inactive' && styles.circleTextInactive,
          ]}>
          {step.id}
        </Text>
      )}
    </View>
  );
};

/**
 * Stepper — hybrid equal-column layout matching the Figma design.
 *
 * Layout: N equal flex:1 step columns, each containing a centred 32 px circle
 * at the top and a centred 2-line label below.
 *
 * Connectors are drawn as position:'absolute' Views inside the row, positioned
 * precisely from the RIGHT EDGE of circle[i] to the LEFT EDGE of circle[i+1].
 * The row width is measured via onLayout; from it:
 *   columnWidth  = rowWidth / N
 *   connector[i].left  = columnWidth × (i + 0.5) + CIRCLE_SIZE / 2
 *   connector[i].width = columnWidth − CIRCLE_SIZE
 *
 * This gives edge-to-edge connectors while step columns remain equal width
 * (providing natural leading/trailing spacing at both ends of the stepper).
 */
const Stepper: React.FC<StepperProps> = ({steps, currentStep, testID}) => {
  const {t} = useTranslation();
  const theme = useScreenTheme('stepper');
  const styles = useMemo(() => createStepperStyles(theme), [theme]);

  const [rowWidth, setRowWidth] = useState(0);
  const onRowLayout = useCallback(
    (e: LayoutChangeEvent) => setRowWidth(e.nativeEvent.layout.width),
    [],
  );

  /**
   * Per-label maxWidth — constrains each Text so it wraps at the optimal
   * word boundary to fit 2 lines, centered under its circle.
   * Scaled from fontSize16 (computeLabelMaxWidth's calibration) to fontSize12.
   */
  const labelMaxWidths = useMemo(
    () => steps.map(step => Math.round(computeLabelMaxWidth(step.label) * LABEL_SCALE)),
    [steps],
  );

  const N = steps.length;
  /** Width of each equal flex:1 step column, derived from measured row width. */
  const columnWidth = rowWidth > 0 ? rowWidth / N : 0;

  /**
   * Absolute connector rectangles — one between each pair of adjacent circles.
   *   left  = right edge of circle i   = columnWidth × (i + 0.5) + CIRCLE_SIZE/2
   *   width = distance between circle edges = columnWidth − CIRCLE_SIZE
   *   top   = (CIRCLE_SIZE − 2) / 2    = 15  (centres 2 px line on 32 px circle)
   */
  const connectors = useMemo(() => {
    if (columnWidth === 0 || N < 2) return [];
    return steps.slice(0, -1).map((step, i) => ({
      stepId: step.id,
      left: columnWidth * (i + 0.5) + CIRCLE_SIZE / 2,
      width: columnWidth - CIRCLE_SIZE,
      active: getStepState(step.id, currentStep) === 'completed',
    }));
  }, [columnWidth, N, steps, currentStep]);

  return (
    <View
      style={styles.container}
      accessibilityRole="none"
      importantForAccessibility="no-hide-descendants"
      testID={testID}>

      {/* Row: equal flex:1 columns + absolutely-positioned connectors */}
      <View style={styles.row} onLayout={onRowLayout}>

        {/* ── Connectors (drawn first so circles render on top) ───────── */}
        {connectors.map(c => (
          <View
            key={`connector-${c.stepId}`}
            style={[
              styles.connector,
              c.active ? styles.connectorActive : styles.connectorInactive,
              {left: c.left, width: c.width},
            ]}
          />
        ))}

        {/* ── Step columns: circle centred at top + label below ────────── */}
        {steps.map((step, index) => {
          const state = getStepState(step.id, currentStep);
          const stateHint =
            state === 'active'
              ? t('createFlow.workOrder.accessibility.stepActive')
              : state === 'completed'
              ? t('createFlow.workOrder.accessibility.stepCompleted')
              : t('createFlow.workOrder.accessibility.stepPending');
          const groupLabel =
            `${step.accessibilityLabel ?? t('createFlow.workOrder.accessibility.stepLabel', {number: step.id, title: step.label})}, ${stateHint}`;
          // On web: tabIndex={0} makes the View a tab stop so keyboard/screen-reader
          // users can navigate to each step. On native: tabIndex is ignored.
          const webTabProps = Platform.OS === 'web' ? {tabIndex: 0 as 0} : {};
          return (
            <View
              key={step.id}
              style={styles.stepGroup}
              accessible={true}
              accessibilityRole="none"
              accessibilityLabel={groupLabel}
              {...webTabProps}>
              <StepCircle
                step={step}
                state={state}
                styles={styles}
                testID={testID}
              />
              <Text
                  style={[
                    styles.label,
                    state === 'active' && styles.labelActive,
                    state === 'completed' && styles.labelCompleted,
                    state === 'inactive' && styles.labelInactive,
                    {maxWidth: labelMaxWidths[index]},
                  ]}
                  numberOfLines={2}
                  importantForAccessibility="no-hide-descendants"
                  accessibilityElementsHidden={true}>
                  {step.label}
                </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default Stepper;
